import { DatePipe } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormField, MatHint } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { Router } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { debounceTime, distinctUntilChanged, from, map, mergeMap, of, takeUntil } from 'rxjs';
import { InstitutionPersonSuggestion } from '../../../api/app-types/institution.app';
import { provideSearchApi, SEARCH_API } from '../../../api/services/search.service';
import { BaseDestroy } from '../../../core/components/base-destroy/base-destroy';
import { LvrButton } from '../../../core/components/lvr-button/lvr-button';
import { LvrInputDirective } from '../../../core/directives/lvr-input.directive';
import { mapDateOrNull } from '../../../shared/helpers/date.func';
import { LvrPaddingDirective } from '../../../shared/padding/padding.directive';

@Component({
	selector: 'lvr-new-institution-person-dialog',
	imports: [
		TranslocoPipe,
		MatDialogModule,
		MatIcon,
		MatFormField,
		LvrInputDirective,
		LvrPaddingDirective,
		ReactiveFormsModule,
		MatAutocompleteModule,
		MatHint,
		MatInput,
		DatePipe,
		LvrButton,
	],
	providers: [provideSearchApi],
	templateUrl: './new-institution-person-dialog.html',
	styleUrl: './new-institution-person-dialog.scss',
})
export class NewInstitutionPersonDialog extends BaseDestroy implements OnInit {
	private readonly router = inject(Router);
	private readonly fb = inject(FormBuilder);
	private readonly api = inject(SEARCH_API);
	public readonly selfRef = inject(MatDialogRef<NewInstitutionPersonDialog>);
	private readonly data = inject(MAT_DIALOG_DATA);
	private readonly institutionId = this.data.institutionId;
	public readonly form = this.initForm();
	public readonly suggestions = signal<InstitutionPersonSuggestion[]>([]);
	private readonly institutionPerson = toSignal<InstitutionPersonSuggestion | string | null>(
		this.form.get('person')!.valueChanges.pipe(debounceTime(300)),
		{ initialValue: null },
	);
	public readonly showWarning = computed(
		() => this.suggestions().length > 0 && typeof this.institutionPerson() === 'string',
	);
	public readonly btnText = computed(() =>
		typeof this.institutionPerson() === 'string'
			? 'institution.person_create.dialog.create'
			: 'institution.person_create.dialog.select',
	);

	public ngOnInit() {
		this.registerFetchSuggestions();
	}

	public displayName(person?: InstitutionPersonSuggestion) {
		if (!person) return '';
		return `${person.firstName} ${person.lastName}`;
	}

	public done() {
		const personValue = this.form.get('person')!.value;

		// Object
		if (personValue && typeof personValue === 'object') {
			const personId = personValue.id;
			const institutionId = personValue.institution.id;
			this.router.navigate(['/desk/institution', institutionId, 'persons', personId]);

			this.selfRef.close();
			return;
		}

		// String
		const institutionId = this.institutionId;
		this.router.navigate(['/desk/institution', institutionId, 'persons', 'add']);

		this.selfRef.close();
	}

	private initForm() {
		return this.fb.group({
			person: this.fb.control<InstitutionPersonSuggestion | string | null>(null, { validators: [Validators.required] }),
		});
	}

	private registerFetchSuggestions() {
		this.form
			.get('person')!
			.valueChanges.pipe(
				takeUntil(this.destroy$),
				debounceTime(300),

				map((value) => (typeof value === 'string' ? value.trim() : '')),
				distinctUntilChanged(),

				mergeMap((searchTerm) => {
					if (!searchTerm) return of(null);

					return from(
						this.api.search({
							searchInstitutions: false,
							searchInstitutionBranches: false,
							searchClientEnvironments: false,
							searchClients: false,
							searchUsers: false,
							searchInstitutionPerson: true,
							searchSponsors: false,
							searchIso: false,
							searchTerm,
						}),
					);
				}),

				map((result) => {
					if (!result) return [];
					return result.entities.map(
						(item): InstitutionPersonSuggestion => ({
							id: item.item.id,
							firstName: item.item.firstName,
							lastName: item.item.name,
							dateOfBirth: mapDateOrNull(item.item.dateOfBirth)?.toString(),
							institution: {
								id: item.institutionBranch?.id ?? 0,
								name: item.institutionBranch?.name ?? '',
								industryName: '',
							},
						}),
					);
				}),
			)
			.subscribe((persons) => this.suggestions.set(persons));
	}
}
